import { API_HEAD, BASE_URL } from '@/lib/apiConfig';

export interface Category {
  id: number;
  name: string;
  description?: string;
  status: 'active' | 'inactive';
  slug: string;
  newsCount: number;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CategoryListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  sortBy?: string;
  order?: 'asc' | 'desc';
}

export interface CategoryListResponse {
  success: boolean;
  data: Category[];
  pagination: {
    total: number;
    totalPages: number;
    currentPage: number;
    limit: number;
  };
  message: string;
}

export interface CategoryResponse {
  success: boolean;
  data: Category;
  message: string;
}

class CategoryService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_HEAD;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const { makeAuthenticatedRequest } = await import('./apiHelper');
    
    const url = `${this.baseUrl}${endpoint}`;
    const baseUrl = BASE_URL;
    
    try {
      const response = await makeAuthenticatedRequest<T>(url, options, baseUrl);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        if (response.status === 401) {
          if (typeof window !== 'undefined') {
            localStorage.removeItem('adminToken');
          }
          throw new Error(errorData.message || 'Không có token xác thực. Vui lòng đăng nhập lại.');
        }
        
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Lỗi không xác định');
    }
  }

  private async publicRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(`${this.baseUrl}${endpoint}`, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Lấy danh sách danh mục
  async getCategories(params: CategoryListParams = {}): Promise<CategoryListResponse> {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    return this.publicRequest<CategoryListResponse>(`/categories?${queryParams.toString()}`);
  }

  // Lấy danh mục theo ID
  async getCategoryById(id: string): Promise<CategoryResponse> {
    return this.publicRequest<CategoryResponse>(`/categories/${id}`);
  }

  // Tạo danh mục mới
  async createCategory(categoryData: Partial<Category>): Promise<CategoryResponse> {
    return this.request<CategoryResponse>('/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData),
    });
  }

  // Cập nhật danh mục
  async updateCategory(id: string, categoryData: Partial<Category>): Promise<CategoryResponse> {
    return this.request<CategoryResponse>(`/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData),
    });
  }

  // Xóa danh mục (đánh dấu xóa)
  async deleteCategory(id: string): Promise<CategoryResponse> {
    return this.request<CategoryResponse>(`/categories/${id}`, {
      method: 'DELETE',
    });
  }

  // Xóa nhiều danh mục
  async deleteManyCategories(ids: number[]): Promise<CategoryResponse> {
    return this.request<CategoryResponse>('/categories/delete-many', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  }

  // Khôi phục danh mục
  async restoreCategory(id: string): Promise<CategoryResponse> {
    return this.request<CategoryResponse>(`/categories/${id}/restore`, {
      method: 'PATCH',
    });
  }

  // Lấy danh sách danh mục trong thùng rác
  async getTrashCategories(params: CategoryListParams = {}): Promise<CategoryListResponse> {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    return this.request<CategoryListResponse>(`/categories/trash?${queryParams.toString()}`);
  }

  // Xóa vĩnh viễn danh mục
  async permanentDeleteCategory(id: string): Promise<CategoryResponse> {
    return this.request<CategoryResponse>(`/categories/${id}/permanent`, {
      method: 'DELETE',
    });
  }
}

const categoryService = new CategoryService();
export default categoryService;

